import axios, { AxiosResponse } from "axios";

// Configuração da API para Users
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://appdav-qa.coffcode.com";

interface User {
  id?: number;
  username: string;
  email: string;
  role: string;
  status: string;
  avatar?: string;
}

class RealUserService {
  private readonly baseURL: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Buscar todos os usuários (List View)
   */
  async getUserList(): Promise<User[]> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<User[]> = await axios.get(`${this.baseURL}/v1/users/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao buscar usuários: ${message}`);
      }
      throw new Error("Erro desconhecido ao buscar usuários");
    }
  }

  /**
   * Buscar usuários para Grid View
   */
  async getUserGrid(): Promise<User[]> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<User[]> = await axios.get(`${this.baseURL}/v1/users/grid`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao buscar grid de usuários: ${message}`);
      }
      throw new Error("Erro desconhecido ao buscar grid de usuários");
    }
  }

  /**
   * Adicionar novo usuário (List)
   */
  async addUserList(userData: Omit<User, "id">): Promise<User> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<User> = await axios.post(
        `${this.baseURL}/api/v1/users/list`,
        userData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao adicionar usuário: ${message}`);
      }
      throw new Error("Erro desconhecido ao adicionar usuário");
    }
  }

  /**
   * Adicionar novo usuário (Grid)
   */
  async addUserGrid(userData: Omit<User, "id">): Promise<User> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<User> = await axios.post(
        `${this.baseURL}/api/v1/users/grid`,
        userData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao adicionar usuário: ${message}`);
      }
      throw new Error("Erro desconhecido ao adicionar usuário");
    }
  }

  /**
   * Atualizar usuário (List)
   */
  async updateUserList(userData: User): Promise<User> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<User> = await axios.put(
        `${this.baseURL}/api/v1/users/list/${userData.id}`,
        userData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao atualizar usuário: ${message}`);
      }
      throw new Error("Erro desconhecido ao atualizar usuário");
    }
  }

  /**
   * Atualizar usuário (Grid)
   */
  async updateUserGrid(userData: User): Promise<User> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<User> = await axios.put(
        `${this.baseURL}/api/v1/users/grid/${userData.id}`,
        userData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao atualizar usuário: ${message}`);
      }
      throw new Error("Erro desconhecido ao atualizar usuário");
    }
  }

  /**
   * Deletar usuário (List)
   */
  async deleteUserList(userId: number): Promise<string> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<{ message: string }> = await axios.delete(
        `${this.baseURL}/api/v1/users/list/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.message;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao deletar usuário: ${message}`);
      }
      throw new Error("Erro desconhecido ao deletar usuário");
    }
  }

  /**
   * Deletar usuário (Grid)
   */
  async deleteUserGrid(userId: number): Promise<string> {
    try {
      const token = this.getAuthToken();
      const response: AxiosResponse<{ message: string }> = await axios.delete(
        `${this.baseURL}/api/v1/users/grid/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data.message;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || error.message;
        throw new Error(`Erro ao deletar usuário: ${message}`);
      }
      throw new Error("Erro desconhecido ao deletar usuário");
    }
  }

  /**
   * Obter token de autenticação
   */
  private getAuthToken(): string {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) {
      throw new Error("Usuário não autenticado");
    }

    const parsed = JSON.parse(authUser);
    if (!parsed.token) {
      throw new Error("Token de autenticação não encontrado");
    }

    return parsed.token;
  }
}

// Instância singleton
export const realUserService = new RealUserService();

export default realUserService;
